import {
  getMembershipPlan,
  isClientOnALaCartePlan,
  isFrictionlessSignupUser,
  type AvailablePlans,
} from '@frontend/api-client';
import type { Client } from '@frontend/api-types';
import { formatCurrencyNatural } from '@frontend/duck-tape';
import dayjs from 'dayjs';

export type DisplayableMembership = {
  includedCredits: number;
  interval: 'mo' | 'yr';
  isOnFreeTier: boolean;
  membershipPlanName: string;
  nextBillingDate: string;
  nextMembershipPlan?: { isCancelled: boolean; membershipPlanName: string };
  pendingChange: boolean;
  price: string;
};

export const buildDisplayableMembership = (
  client?: Client,
  availablePlans?: AvailablePlans,
): DisplayableMembership | undefined => {
  if (!client) return undefined;

  const membershipPlan = getMembershipPlan(client, { availablePlans });
  if (!membershipPlan) return undefined;

  const membershipPlanName = membershipPlan?.label || '';
  const price = formatCurrencyNatural(client.price);
  const interval = client.priceInterval === 'month' ? 'mo' : 'yr';
  const nextBillingDate = client.subscriptionEnd ? dayjs(client.subscriptionEnd).format('MMMM D') : '';
  const includedCredits = membershipPlan.creditsPerMonth ?? 0;
  const pendingSubscriptionChange = client?.pendingSubscriptionChange;
  const plan = pendingSubscriptionChange
    ? getMembershipPlan({ isEmployeePrice: false, plan: pendingSubscriptionChange.targetPlan }, { availablePlans })
    : undefined;
  const nextMembershipPlan = plan
    ? { isCancelled: plan.planCode === 'A_LA_CARTE', membershipPlanName: plan.label }
    : undefined;

  return {
    includedCredits,
    interval,
    isOnFreeTier: isClientOnALaCartePlan(client) || isFrictionlessSignupUser(client),
    membershipPlanName,
    nextBillingDate,
    nextMembershipPlan,
    pendingChange: Boolean(client.pendingSubscriptionChange),
    price,
  } satisfies DisplayableMembership;
};
